import { 
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  Timestamp,
  where,
  doc,
  updateDoc,
  getDoc,
  deleteDoc,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Company, CompanyDependencies, CompanyAuditEntry, DuplicateGroup, RevenueMergeStrategy } from '../types';
import companyEnrichmentService from './companyEnrichmentService';

// ─── Helpers ──────────────────────────────────────────────────────────

/** Normalize a company name for duplicate comparison */
function normalizeName(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/\s*(gmbh|ag|kg|ohg|ug|e\.v\.|e\.k\.|gbr|inc|llc|ltd|corp|co\.|& co\.?)\s*/gi, '')
    .replace(/[.\-_,]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Similarity score between two strings (0-1) using Levenshtein + token overlap */
function similarityScore(a: string, b: string): number {
  if (a === b) return 1;
  const longer = a.length >= b.length ? a : b;
  const shorter = a.length < b.length ? a : b;
  if (longer.length === 0) return 1;

  if (longer.startsWith(shorter) || shorter.startsWith(longer)) {
    return Math.max(0.8, shorter.length / longer.length);
  }

  const tokensA = a.split(/\s+/);
  const tokensB = b.split(/\s+/);
  if (tokensA.length > 1 || tokensB.length > 1) {
    const setA = new Set(tokensA);
    const setB = new Set(tokensB);
    let overlap = 0;
    for (const t of setA) if (setB.has(t)) overlap++;
    const tokenScore = overlap / Math.max(setA.size, setB.size);
    if (tokenScore >= 0.5) return Math.max(0.75, tokenScore);
  }

  const matrix: number[][] = [];
  for (let i = 0; i <= shorter.length; i++) matrix[i] = [i];
  for (let j = 0; j <= longer.length; j++) matrix[0][j] = j;
  for (let i = 1; i <= shorter.length; i++) {
    for (let j = 1; j <= longer.length; j++) {
      if (shorter[i - 1] === longer[j - 1]) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }
  return 1 - matrix[shorter.length][longer.length] / longer.length;
}

/** Write an audit log entry */
async function writeAuditLog(entry: Omit<CompanyAuditEntry, 'id'>): Promise<void> {
  const payload: any = {
    ...entry,
    performedAt: Timestamp.now().toDate()
  };
  await addDoc(collection(db, 'companyAuditLog'), payload);
}

/** Commit operations in chunked batches (max 450 per batch for safety) */
async function commitChunkedBatch(
  operations: Array<{ type: 'update' | 'delete'; ref: any; data?: any }>
): Promise<void> {
  const CHUNK_SIZE = 450;
  for (let i = 0; i < operations.length; i += CHUNK_SIZE) {
    const chunk = operations.slice(i, i + CHUNK_SIZE);
    const batch = writeBatch(db);
    for (const op of chunk) {
      if (op.type === 'update') {
        batch.update(op.ref, op.data);
      } else if (op.type === 'delete') {
        batch.delete(op.ref);
      }
    }
    await batch.commit();
  }
}

// ─── Main Service ─────────────────────────────────────────────────────

export const companiesService = {
  async getCompanies(orgId?: string, includeArchived = false): Promise<Company[]> {
    const base = collection(db, 'companies');
    const q = (orgId && orgId.trim()) 
      ? query(base, where('orgId', '==', orgId), orderBy('name')) 
      : query(base, orderBy('name'));
    const snap = await getDocs(q);
    let companies = snap.docs.map(d => ({ ...(d.data() as any), id: d.id })) as Company[];
    
    // Filter out archived unless explicitly requested
    if (!includeArchived) {
      companies = companies.filter(c => !c.archived);
    }
    
    return companies;
  },

  async findByDomain(domain: string, orgId?: string): Promise<Company | null> {
    const base = collection(db, 'companies');
    const q = (orgId && orgId.trim()) 
      ? query(base, where('orgId', '==', orgId), where('domain', '==', domain)) 
      : query(base, where('domain', '==', domain));
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const d = snap.docs[0];
    return { ...(d.data() as any), id: d.id } as Company;
  },

  async createCompany(company: Omit<Company, 'id' | 'createdAt' | 'updatedAt'> & { orgId?: string }): Promise<string> {
    const payload: any = {
      ...company,
      createdAt: Timestamp.now().toDate(),
      updatedAt: Timestamp.now().toDate()
    };
    Object.keys(payload).forEach((k) => {
      if (payload[k] === undefined) {
        delete payload[k];
      }
    });
    if (payload.id === '' || typeof payload.id !== 'undefined') {
      delete payload.id;
    }
    const docRef = await addDoc(collection(db, 'companies'), payload);
    return docRef.id;
  },

  async updateCompany(companyId: string, updates: Partial<Company>): Promise<void> {
    const ref = doc(db, 'companies', companyId);
    const payload: any = { ...updates, updatedAt: Timestamp.now().toDate() };
    
    Object.keys(payload).forEach((k) => {
      if (payload[k] === undefined) delete payload[k];
    });
    
    await updateDoc(ref, payload);
  },

  /**
   * Enrich company data with AI-gathered information
   */
  async enrichCompany(companyId: string): Promise<Company> {
    const ref = doc(db, 'companies', companyId);
    const companySnap = await getDoc(ref);
    
    if (!companySnap.exists()) {
      throw new Error(`Company ${companyId} not found`);
    }
    
    const company = { id: companySnap.id, ...companySnap.data() } as Company;
    const enrichmentData = await companyEnrichmentService.enrichCompany(company);
    
    await updateDoc(ref, {
      enrichment: enrichmentData,
      updatedAt: Timestamp.now().toDate()
    });
    
    return { ...company, enrichment: enrichmentData };
  },

  async searchCompanies(searchQuery: string): Promise<Company[]> {
    return await companyEnrichmentService.searchCompanies(searchQuery);
  },

  async analyzeCompetitors(companyId: string): Promise<{
    competitors: string[];
    analysis: string;
  }> {
    const ref = doc(db, 'companies', companyId);
    const companySnap = await getDoc(ref);
    
    if (!companySnap.exists()) {
      throw new Error(`Company ${companyId} not found`);
    }
    
    const company = companySnap.data() as Company;
    return await companyEnrichmentService.analyzeCompetitors(company.name);
  },

  async getCompanyById(companyId: string): Promise<Company | null> {
    const ref = doc(db, 'companies', companyId);
    const companySnap = await getDoc(ref);
    
    if (!companySnap.exists()) {
      return null;
    }
    
    return { id: companySnap.id, ...companySnap.data() } as Company;
  },

  // ─── Data Cleanup: Duplicate Detection ───────────────────────────────

  /**
   * Find potential duplicate companies by name similarity and domain match.
   */
  async findDuplicates(orgId: string): Promise<DuplicateGroup[]> {
    const companies = await this.getCompanies(orgId, false); // exclude archived
    const groups: DuplicateGroup[] = [];
    const processed = new Set<string>();

    for (let i = 0; i < companies.length; i++) {
      if (processed.has(companies[i].id)) continue;

      const nameA = normalizeName(companies[i].name);
      const domainA = (companies[i].domain || '').toLowerCase().trim();
      const duplicates: Company[] = [companies[i]];
      let bestReason: DuplicateGroup['reason'] = 'similar_name';
      let bestScore = 0;

      for (let j = i + 1; j < companies.length; j++) {
        if (processed.has(companies[j].id)) continue;

        const nameB = normalizeName(companies[j].name);
        const domainB = (companies[j].domain || '').toLowerCase().trim();

        const nameSim = similarityScore(nameA, nameB);
        const domainMatch = domainA && domainB && domainA === domainB;

        if (nameSim >= 0.75 || domainMatch) {
          duplicates.push(companies[j]);
          processed.add(companies[j].id);

          const score = Math.max(nameSim, domainMatch ? 1 : 0);
          if (score > bestScore) bestScore = score;

          if (nameSim >= 0.75 && domainMatch) {
            bestReason = 'both';
          } else if (domainMatch) {
            bestReason = 'same_domain';
          }
        }
      }

      if (duplicates.length > 1) {
        processed.add(companies[i].id);
        groups.push({
          companies: duplicates,
          reason: bestReason,
          similarity: bestScore
        });
      }
    }

    // Sort by similarity desc
    groups.sort((a, b) => b.similarity - a.similarity);
    return groups;
  },

  // ─── Data Cleanup: Dependency Check ──────────────────────────────────

  /**
   * Check all data that references a company. Used before archive/delete/merge.
   */
  async getCompanyDependencies(companyId: string, orgId: string): Promise<CompanyDependencies> {
    const result: CompanyDependencies = {
      opportunities: { total: 0, open: 0, won: 0, items: [] },
      leads: { total: 0, items: [] },
      tasks: { total: 0, open: 0 },
      timeEntries: 0,
      engagements: 0,
      expenses: { total: 0, amount: 0 },
      customerIntelligence: 0,
      hasActiveContracts: false,
      hasPaymentSchedules: false,
      canSafelyDelete: true
    };

    try {
      // 1. Opportunities
      const oppsQ = query(
        collection(db, 'opportunities'),
        where('companyId', '==', companyId)
      );
      const oppsSnap = await getDocs(oppsQ);
      result.opportunities.total = oppsSnap.size;
      oppsSnap.docs.forEach(d => {
        const data = d.data();
        const stage = (data.stage || '').toLowerCase();
        if (stage === 'won' || stage === 'closed_won') {
          result.opportunities.won++;
        } else if (stage !== 'lost' && stage !== 'closed_lost') {
          result.opportunities.open++;
        }
        result.opportunities.items.push({
          id: d.id,
          title: data.title || data.name || 'Unnamed',
          stage: data.stage || 'unknown'
        });
      });

      // 2. Leads (search by companyId field)
      const leadsQ = query(
        collection(db, 'leads'),
        where('companyId', '==', companyId)
      );
      const leadsSnap = await getDocs(leadsQ);
      result.leads.total = leadsSnap.size;
      leadsSnap.docs.forEach(d => {
        const data = d.data();
        result.leads.items.push({
          id: d.id,
          name: `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Unnamed'
        });
      });

      // 3. Tasks
      const tasksQ = query(
        collection(db, 'tasks'),
        where('companyId', '==', companyId)
      );
      const tasksSnap = await getDocs(tasksQ);
      result.tasks.total = tasksSnap.size;
      tasksSnap.docs.forEach(d => {
        const data = d.data();
        if (data.status !== 'completed' && data.status !== 'done') {
          result.tasks.open++;
        }
      });

      // 4. Time entries
      const timeQ = query(
        collection(db, 'timeEntries'),
        where('companyId', '==', companyId)
      );
      const timeSnap = await getDocs(timeQ);
      result.timeEntries = timeSnap.size;

      // 5. Engagements
      const engQ = query(
        collection(db, 'engagements'),
        where('customerId', '==', companyId)
      );
      const engSnap = await getDocs(engQ);
      result.engagements = engSnap.size;

      // 6. Expenses
      const expQ = query(
        collection(db, 'expenses'),
        where('companyId', '==', companyId)
      );
      const expSnap = await getDocs(expQ);
      result.expenses.total = expSnap.size;
      expSnap.docs.forEach(d => {
        const data = d.data();
        result.expenses.amount += (data.amount || 0);
      });

      // 7. Customer Intelligence
      try {
        const ciQ = query(
          collection(db, `organizations/${orgId}/customerIntelligence`),
          where('linkedCompanyId', '==', companyId)
        );
        const ciSnap = await getDocs(ciQ);
        result.customerIntelligence = ciSnap.size;
      } catch {
        // Collection might not exist
      }

      // 8. Check payment schedules & active contracts from company doc
      const companyDoc = await getDoc(doc(db, 'companies', companyId));
      if (companyDoc.exists()) {
        const data = companyDoc.data();
        const schedules = data.paymentSchedules || [];
        result.hasPaymentSchedules = schedules.length > 0;
        result.hasActiveContracts = schedules.some((s: any) => s.isActive);
        
        // Also check contractEnd
        if (data.contractEnd) {
          const contractEnd = data.contractEnd.toDate ? data.contractEnd.toDate() : new Date(data.contractEnd);
          if (contractEnd > new Date()) {
            result.hasActiveContracts = true;
          }
        }
      }

      // Determine if safe to delete
      result.canSafelyDelete = result.opportunities.open === 0 && !result.hasActiveContracts;

    } catch (error) {
      console.error('Error checking company dependencies:', error);
    }

    return result;
  },

  // ─── Data Cleanup: Archive (Soft Delete) ─────────────────────────────

  /**
   * Archive a company (soft delete). Hidden from default views but can be restored.
   */
  async archiveCompany(companyId: string, orgId: string, userId: string, userName?: string): Promise<void> {
    const companyRef = doc(db, 'companies', companyId);
    const companySnap = await getDoc(companyRef);
    
    if (!companySnap.exists()) {
      throw new Error('Company not found');
    }

    const companyData = companySnap.data();

    await updateDoc(companyRef, {
      archived: true,
      archivedAt: Timestamp.now().toDate(),
      archivedBy: userId,
      updatedAt: Timestamp.now().toDate()
    });

    await writeAuditLog({
      action: 'archive',
      performedBy: userId,
      performedByName: userName,
      performedAt: new Date(),
      orgId,
      primaryCompanyId: companyId,
      companyName: companyData.name || 'Unknown',
      details: {}
    });
  },

  /**
   * Restore an archived company.
   */
  async restoreCompany(companyId: string, orgId: string, userId: string, userName?: string): Promise<void> {
    const companyRef = doc(db, 'companies', companyId);
    const companySnap = await getDoc(companyRef);
    
    if (!companySnap.exists()) {
      throw new Error('Company not found');
    }

    const companyData = companySnap.data();

    await updateDoc(companyRef, {
      archived: false,
      archivedAt: null,
      archivedBy: null,
      updatedAt: Timestamp.now().toDate()
    });

    await writeAuditLog({
      action: 'unarchive',
      performedBy: userId,
      performedByName: userName,
      performedAt: new Date(),
      orgId,
      primaryCompanyId: companyId,
      companyName: companyData.name || 'Unknown',
      details: {}
    });
  },

  // ─── Data Cleanup: Permanent Delete ──────────────────────────────────

  /**
   * Permanently delete a company. Only works if company is archived and has no blocking dependencies.
   */
  async permanentlyDeleteCompany(
    companyId: string,
    orgId: string,
    userId: string,
    userName?: string
  ): Promise<void> {
    const companyRef = doc(db, 'companies', companyId);
    const companySnap = await getDoc(companyRef);
    
    if (!companySnap.exists()) {
      throw new Error('Company not found');
    }

    const companyData = companySnap.data();
    
    if (!companyData.archived) {
      throw new Error('Company must be archived before permanent deletion');
    }

    // Final safety check
    const deps = await this.getCompanyDependencies(companyId, orgId);
    if (!deps.canSafelyDelete) {
      throw new Error('Company has open opportunities or active contracts. Cannot delete.');
    }

    // Write audit log BEFORE deletion
    await writeAuditLog({
      action: 'delete',
      performedBy: userId,
      performedByName: userName,
      performedAt: new Date(),
      orgId,
      primaryCompanyId: companyId,
      companyName: companyData.name || 'Unknown',
      details: {
        dependencies: {
          opportunities: deps.opportunities.total,
          leads: deps.leads.total,
          tasks: deps.tasks.total,
          timeEntries: deps.timeEntries,
          engagements: deps.engagements,
          expenses: deps.expenses.total
        }
      }
    });

    // Delete the company document
    await deleteDoc(companyRef);
  },

  // ─── Data Cleanup: Merge Companies ───────────────────────────────────

  /**
   * Merge secondary company into primary. Updates all references, merges fields, archives secondary.
   */
  async mergeCompanies(
    primaryId: string,
    secondaryId: string,
    fieldOverrides: Partial<Company>,
    revenueMergeStrategy: RevenueMergeStrategy,
    orgId: string,
    userId: string,
    userName?: string,
    onProgress?: (current: number, total: number) => void
  ): Promise<void> {
    // Load both companies
    const [primarySnap, secondarySnap] = await Promise.all([
      getDoc(doc(db, 'companies', primaryId)),
      getDoc(doc(db, 'companies', secondaryId))
    ]);

    if (!primarySnap.exists() || !secondarySnap.exists()) {
      throw new Error('One or both companies not found');
    }

    const primary = { id: primarySnap.id, ...primarySnap.data() } as Company & Record<string, any>;
    const secondary = { id: secondarySnap.id, ...secondarySnap.data() } as Company & Record<string, any>;

    // Write "merge_started" audit entry
    await writeAuditLog({
      action: 'merge',
      performedBy: userId,
      performedByName: userName,
      performedAt: new Date(),
      orgId,
      primaryCompanyId: primaryId,
      secondaryCompanyId: secondaryId,
      companyName: primary.name,
      secondaryCompanyName: secondary.name,
      details: { status: 'started', fieldOverrides, revenueMergeStrategy }
    });

    // Collect all batch operations
    const operations: Array<{ type: 'update' | 'delete'; ref: any; data?: any }> = [];
    let totalOps = 0;

    // --- Update references in related collections ---

    // 1. Opportunities: update companyId AND denormalized company object
    const oppsQ = query(collection(db, 'opportunities'), where('companyId', '==', secondaryId));
    const oppsSnap = await getDocs(oppsQ);
    oppsSnap.docs.forEach(d => {
      operations.push({
        type: 'update',
        ref: doc(db, 'opportunities', d.id),
        data: {
          companyId: primaryId,
          company: { id: primaryId, name: fieldOverrides.name || primary.name },
          updatedAt: Timestamp.now().toDate()
        }
      });
    });

    // 2. Leads: update companyId
    const leadsQ = query(collection(db, 'leads'), where('companyId', '==', secondaryId));
    const leadsSnap = await getDocs(leadsQ);
    leadsSnap.docs.forEach(d => {
      operations.push({
        type: 'update',
        ref: doc(db, 'leads', d.id),
        data: {
          companyId: primaryId,
          company: fieldOverrides.name || primary.name,
          updatedAt: Timestamp.now().toDate()
        }
      });
    });

    // 3. Tasks
    const tasksQ = query(collection(db, 'tasks'), where('companyId', '==', secondaryId));
    const tasksSnap = await getDocs(tasksQ);
    tasksSnap.docs.forEach(d => {
      operations.push({
        type: 'update',
        ref: doc(db, 'tasks', d.id),
        data: { companyId: primaryId, updatedAt: Timestamp.now().toDate() }
      });
    });

    // 4. Time entries: update companyId AND denormalized company object
    const timeQ = query(collection(db, 'timeEntries'), where('companyId', '==', secondaryId));
    const timeSnap = await getDocs(timeQ);
    timeSnap.docs.forEach(d => {
      operations.push({
        type: 'update',
        ref: doc(db, 'timeEntries', d.id),
        data: {
          companyId: primaryId,
          company: { id: primaryId, name: fieldOverrides.name || primary.name },
          updatedAt: Timestamp.now().toDate()
        }
      });
    });

    // 5. Engagements
    const engQ = query(collection(db, 'engagements'), where('customerId', '==', secondaryId));
    const engSnap = await getDocs(engQ);
    engSnap.docs.forEach(d => {
      operations.push({
        type: 'update',
        ref: doc(db, 'engagements', d.id),
        data: { customerId: primaryId, updatedAt: Timestamp.now().toDate() }
      });
    });

    // 6. Expenses
    const expQ = query(collection(db, 'expenses'), where('companyId', '==', secondaryId));
    const expSnap = await getDocs(expQ);
    expSnap.docs.forEach(d => {
      operations.push({
        type: 'update',
        ref: doc(db, 'expenses', d.id),
        data: { companyId: primaryId, updatedAt: Timestamp.now().toDate() }
      });
    });

    // 7. Customer Intelligence
    try {
      const ciQ = query(
        collection(db, `organizations/${orgId}/customerIntelligence`),
        where('linkedCompanyId', '==', secondaryId)
      );
      const ciSnap = await getDocs(ciQ);
      ciSnap.docs.forEach(d => {
        operations.push({
          type: 'update',
          ref: doc(db, `organizations/${orgId}/customerIntelligence`, d.id),
          data: { linkedCompanyId: primaryId }
        });
      });
    } catch {
      // Collection might not exist
    }

    totalOps = operations.length;

    // --- Merge company fields ---
    const mergedFields: any = {
      ...fieldOverrides,
      updatedAt: Timestamp.now().toDate()
    };

    // Handle revenue merge
    const primaryRevenue = (primary as any).monthlyRevenue || {};
    const secondaryRevenue = (secondary as any).monthlyRevenue || {};
    
    if (revenueMergeStrategy === 'add') {
      const merged: Record<string, number> = { ...primaryRevenue };
      Object.entries(secondaryRevenue).forEach(([month, amount]) => {
        merged[month] = (merged[month] || 0) + (amount as number);
      });
      mergedFields.monthlyRevenue = merged;
    } else if (revenueMergeStrategy === 'primary') {
      mergedFields.monthlyRevenue = primaryRevenue;
    }
    // 'pick' strategy: handled in UI, fieldOverrides already contains the picked values

    // Merge payment schedules (concatenate)
    const primarySchedules = (primary as any).paymentSchedules || [];
    const secondarySchedules = (secondary as any).paymentSchedules || [];
    if (secondarySchedules.length > 0) {
      mergedFields.paymentSchedules = [...primarySchedules, ...secondarySchedules];
    }

    // Update primary company
    operations.push({
      type: 'update',
      ref: doc(db, 'companies', primaryId),
      data: mergedFields
    });

    // Archive secondary company (don't delete, keep as audit trail)
    operations.push({
      type: 'update',
      ref: doc(db, 'companies', secondaryId),
      data: {
        archived: true,
        archivedAt: Timestamp.now().toDate(),
        archivedBy: userId,
        updatedAt: Timestamp.now().toDate()
      }
    });

    // Commit all operations in chunks
    await commitChunkedBatch(operations);

    // Report progress
    if (onProgress) {
      onProgress(totalOps, totalOps);
    }

    // Write "merge_completed" audit entry
    await writeAuditLog({
      action: 'merge',
      performedBy: userId,
      performedByName: userName,
      performedAt: new Date(),
      orgId,
      primaryCompanyId: primaryId,
      secondaryCompanyId: secondaryId,
      companyName: primary.name,
      secondaryCompanyName: secondary.name,
      details: {
        status: 'completed',
        recordsUpdated: totalOps,
        fieldOverrides,
        revenueMergeStrategy
      }
    });
  }
};


